/**
 * Owner glance presentation helpers — plan/44.
 */
import type { DeskGlance, DeskGlanceQueueMetric } from "@/shared/api/desk";

export type OwnerGlanceChip = {
  key:
    | "salesToday"
    | "toCollect"
    | "toDeliver"
    | "toPay"
    | "toReceive"
    | "lowStock";
  to: string;
  label: string;
  /** Money-only chip (Sales today). */
  amount?: string;
  /** Prior-day amount for expand vs-period (Sales today). */
  yesterdayAmount?: string;
  /** Last 7 days oldest→newest for sparkline. */
  last7?: string[];
  count?: number;
  outstanding?: string;
};

/** SVG polyline points for a compact sparkline (viewBox 0 0 84 28). */
export function sparklinePoints(values: readonly string[]): string {
  if (values.length === 0) return "";
  const nums = values.map((v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
  });
  const max = Math.max(...nums, 1);
  const w = 84;
  const h = 28;
  const pad = 2;
  const n = nums.length;
  return nums
    .map((v, i) => {
      const x = n === 1 ? w / 2 : (i / (n - 1)) * w;
      const y = h - pad - (v / max) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

/** Human vs-yesterday line for expand — deterministic, no fake history. */
export function vsYesterdayLabel(
  amount: string,
  yesterdayAmount: string,
  currencyCode: string,
): string {
  const today = Number(amount);
  const yday = Number(yesterdayAmount);
  if (!Number.isFinite(today) || !Number.isFinite(yday)) {
    return "vs yesterday · —";
  }
  const delta = today - yday;
  const yFmt =
    formatGlanceMoney(yesterdayAmount, currencyCode, {
      maxFractionDigits: 0,
      zeroOk: true,
    }) ?? yesterdayAmount;
  if (delta === 0) return `vs yesterday · same (${yFmt})`;
  const dFmt =
    formatGlanceMoney(String(Math.abs(delta)), currencyCode, {
      maxFractionDigits: 0,
      zeroOk: true,
    }) ?? String(Math.abs(delta));
  const sign = delta > 0 ? "+" : "−";
  return `vs yesterday · ${sign}${dFmt} (was ${yFmt})`;
}

export function formatGlanceMoney(
  outstanding: string | undefined,
  currencyCode: string,
  opts?: { maxFractionDigits?: number; zeroOk?: boolean },
): string | null {
  if (outstanding == null) return null;
  const n = Number(outstanding);
  if (!Number.isFinite(n)) return null;
  if (n < 0) return null;
  if (n === 0 && !opts?.zeroOk) return null;
  const maxFractionDigits = opts?.maxFractionDigits ?? 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode || "PHP",
      maximumFractionDigits: maxFractionDigits,
      minimumFractionDigits: maxFractionDigits > 0 ? 2 : 0,
    }).format(n);
  } catch {
    return `${currencyCode} ${outstanding}`;
  }
}

export function collapsedChipValue(
  metric: DeskGlanceQueueMetric,
  currencyCode: string,
): string {
  const money = formatGlanceMoney(metric.outstanding, currencyCode);
  return money != null ? `${metric.count} · ${money}` : String(metric.count);
}

export function chipDisplayValue(
  chip: OwnerGlanceChip,
  currencyCode: string,
): string {
  if (chip.amount != null) {
    return (
      formatGlanceMoney(chip.amount, currencyCode, { zeroOk: true }) ??
      chip.amount
    );
  }
  return collapsedChipValue(
    { count: chip.count ?? 0, outstanding: chip.outstanding },
    currencyCode,
  );
}

export function ownerGlanceChips(
  companySlug: string,
  glance: DeskGlance,
): OwnerGlanceChip[] {
  const chips: OwnerGlanceChip[] = [];
  if (glance.salesToday) {
    chips.push({
      key: "salesToday",
      to: `/c/${companySlug}/pos`,
      label: "Sales today",
      amount: glance.salesToday.amount,
      yesterdayAmount: glance.salesToday.yesterdayAmount,
      last7: glance.salesToday.last7,
    });
  }
  if (glance.lowStock) {
    chips.push({
      key: "lowStock",
      to: `/c/${companySlug}/inventory?lowStock=1`,
      label: "Low stock",
      count: glance.lowStock.count,
    });
  }
  if (glance.toCollect) {
    chips.push({
      key: "toCollect",
      to: `/c/${companySlug}/sales/to-collect`,
      label: "To collect",
      count: glance.toCollect.count,
      outstanding: glance.toCollect.outstanding,
    });
  }
  if (glance.toDeliver) {
    chips.push({
      key: "toDeliver",
      to: `/c/${companySlug}/sales/to-deliver`,
      label: "To deliver",
      count: glance.toDeliver.count,
    });
  }
  if (glance.toPay) {
    chips.push({
      key: "toPay",
      to: `/c/${companySlug}/purchasing/to-pay`,
      label: "To pay",
      count: glance.toPay.count,
      outstanding: glance.toPay.outstanding,
    });
  }
  if (glance.toReceive) {
    chips.push({
      key: "toReceive",
      to: `/c/${companySlug}/purchasing/to-receive`,
      label: "To receive",
      count: glance.toReceive.count,
    });
  }
  return chips;
}
